import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { api, beginAuthTransition } from "./api";
import { Login, Layout, useDataset, usePeriod } from "./shell";
import Overview from "./pages/Overview";
import ReportFreshness from "./components/ReportFreshness";
import { setReportContext } from "./lib/reportCache";
import Policies from "./pages/Policies";
import { Sellers, SellerDetail } from "./pages/Sellers";
import { Regions, RegionDetail } from "./pages/Regions";
import { TargetPerformance, TargetEntry } from "./pages/Targets";
import { Branches, Companies, CompanyDetail } from "./pages/BranchesCompanies";
import { Customers, CustomerDetail } from "./pages/Customers";
import Quality from "./pages/Quality";
import ImportPage from "./pages/Import";
import Organization from "./pages/Organization";
import Settings from "./pages/Settings";
import Renewals from "./pages/Renewals";
import PortfolioAnalysis from "./pages/PortfolioAnalysis";
import CrossSell from "./pages/CrossSell";
import BusinessClassification from "./pages/BusinessClassification";
import { can } from "./lib/permissions";
import "./App.css";

function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [reportRevision, setReportRevision] = useState(0);
  const dataset = useDataset(user);
  const per = usePeriod();
  const reportContextKey = JSON.stringify(per.periodParams);
  useEffect(() => { setReportContext(reportContextKey); }, [reportContextKey]);

  useEffect(() => {
    const token = localStorage.getItem("imc_token");
    if (!token) { setChecking(false); return; }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {
      localStorage.removeItem("imc_token");
    }).finally(() => setChecking(false));
  }, []);

  const logout = async () => {
    beginAuthTransition();
    try { await api.post("/auth/logout"); } catch (e) {}
    finally {
      localStorage.removeItem("imc_token");
      setUser(null);
    }
  };
  const beginImpersonation = ({ user: target, token }) => {
    localStorage.setItem("imc_token", token);
    setUser(target);
    window.location.assign("/");
  };
  const exitImpersonation = async () => {
    beginAuthTransition();
    const response = await api.post("/auth/impersonation/exit");
    localStorage.setItem("imc_token", response.data.token);
    setUser(response.data.user);
    window.location.assign("/settings");
  };

  if (checking) return <div className="loading">Güvenli oturum kontrol ediliyor...</div>;
  if (!user) return <Login onLogin={setUser} />;
  const allowed = (permission, element, baseRoles = null) => can(user, permission) && (!baseRoles || baseRoles.includes(user.role)) ? element : <main className="content"><div data-testid="permission-denied" className="error">Bu ekran için rol yetkiniz yok.</div></main>;

  return (
    <Layout user={user} dataset={dataset} onLogout={logout} onExitImpersonation={exitImpersonation}>
      <ReportFreshness contextKey={reportContextKey} onRefresh={() => setReportRevision((value) => value + 1)} />
      <Routes key={reportRevision}>
        <Route path="/" element={allowed("overview.view", <Overview dataset={dataset} {...per} />)} />
        <Route path="/targets" element={allowed("targets.performance.view", <TargetPerformance user={user} dataset={dataset} {...per} />)} />
        <Route path="/target-entry" element={allowed("targets.entry.view", <TargetEntry user={user} />, ["admin","manager"])} />
        <Route path="/regions" element={allowed("regions.view", <Regions user={user} dataset={dataset} {...per} />)} />
        <Route path="/regions/:id" element={allowed("regions.view", <RegionDetail user={user} dataset={dataset} {...per} />)} />
        <Route path="/sellers" element={allowed("sellers.view", <Sellers dataset={dataset} {...per} />)} />
        <Route path="/sellers/:id" element={allowed("sellers.view", <SellerDetail dataset={dataset} {...per} />)} />
        <Route path="/branches" element={allowed("branches.view", <Branches dataset={dataset} {...per} />)} />
        <Route path="/companies" element={allowed("companies.view", <Companies dataset={dataset} {...per} />)} />
        <Route path="/companies/:id" element={allowed("companies.view", <CompanyDetail dataset={dataset} {...per} />)} />
        <Route path="/policies" element={allowed("policies.list", <Policies user={user} dataset={dataset} {...per} />)} />
        <Route path="/business-classification" element={allowed("business_classification.view", <BusinessClassification user={user} dataset={dataset} {...per} />)} />
        <Route path="/renewals" element={allowed("renewals.view", <Renewals user={user} />)} />
        <Route path="/portfolio-analysis" element={allowed("portfolio.view", <PortfolioAnalysis user={user} dataset={dataset} periodProps={per} />)} />
        <Route path="/cross-sell" element={allowed("crosssell.view", <CrossSell user={user} />)} />
        <Route path="/customers" element={allowed("customers.view", <Customers user={user} />, ["admin","manager","regional_manager"])} />
        <Route path="/customers/:id" element={allowed("customers.view", <CustomerDetail user={user} />, ["admin","manager","regional_manager"])} />
        <Route path="/import" element={allowed("imports.history", <ImportPage user={user} />, ["admin", "manager"])} />
        <Route path="/organization" element={allowed("organization.view", <Organization user={user} />, ["admin","manager"])} />
        <Route path="/quality" element={allowed("quality.view", <Quality user={user} />)} />
        <Route path="/settings" element={<Settings user={user} onImpersonate={beginImpersonation} onUserRefresh={async()=>{const response=await api.get("/auth/me");setUser(response.data);}} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

export default function Root() {
  return <BrowserRouter><App /></BrowserRouter>;
}
